import React, { useEffect, useState } from "react";
import { getEducation, getExperience, getProfile } from "../../../../api/profileAPI";
import ExperienceMediaCards from "../../../ExperienceMediaCards";
import { sortExperienceGrouped, calcDuration } from "../../../../utils/sortExperience";

// ── helpers ──────────────────────────────────────────────────────────────────
const MONTHS = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Mei: 4,
  Jun: 5, Jul: 6, Aug: 7, Agu: 7, Sep: 8, Oct: 9, Okt: 9, Nov: 10, Dec: 11, Des: 11,
};

function parseDate(str) {
  if (!str) return new Date(0);
  const t = String(str).trim();
  if (/present|sekarang|now/i.test(t)) return new Date(9999, 11, 31);
  if (/^\d{4}$/.test(t)) return new Date(+t, 0, 1);
  const [mon, yr] = t.split(/[\s-]+/);
  return new Date(+yr || 0, MONTHS[mon] ?? 0, 1);
}

function fmtDate(str) {
  if (!str) return "";
  const t = String(str).trim();
  if (/present|sekarang|now/i.test(t)) return "Present";
  // ISO date string from DB (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) {
    return new Date(t).toLocaleDateString("en-US", { month: "short", year: "numeric" });
  }
  return t;
}

// ── Experience ────────────────────────────────────────────────────────────────
const ExperienceSection = ({ items }) => {
  const companies = sortExperienceGrouped(items);

  return (
    <section className="mb-10">
      <SectionHeading
        label="Experience"
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        }
      />

      <div className="relative ml-3">
        {/* Vertical line */}
        <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-blue-600 via-zinc-700 to-transparent" />

        <div className="space-y-6">
          {companies.map(([company, roles]) => (
            <div key={company} className="relative pl-8">
              {/* Timeline dot */}
              <div className="absolute left-0 top-1.5 -translate-x-1/2 w-3 h-3 rounded-full bg-blue-600 border-2 border-[#111116] shadow-[0_0_8px_rgba(59,130,246,0.5)]" />

              {/* Company name + total duration */}
              <div className="flex items-center gap-2 mb-2">
                <p className="text-blue-400 font-black text-sm">{company}</p>
                {(() => {
                  const starts = roles.map(r => r.start_date || r.start).filter(Boolean);
                  const hasCurrent = roles.some(r => r.is_current);
                  const ends = roles.filter(r => !r.is_current).map(r => r.end_date || r.end).filter(Boolean);
                  const earliestStart = starts.length ? starts.reduce((a, b) => a < b ? a : b) : null;
                  const latestEnd = ends.length ? ends.reduce((a, b) => a > b ? a : b) : null;
                  const d = calcDuration(earliestStart, latestEnd, hasCurrent);
                  return d ? <span className="text-[10px] text-zinc-500 bg-zinc-800 rounded-full px-2 py-0.5">{d} total</span> : null;
                })()}
              </div>

              {/* Roles */}
              <div className="space-y-4">
                {roles.map((r, i) => {
                    const startLabel = fmtDate(r.start_date || r.start);
                    const endLabel = r.is_current ? "Present" : fmtDate(r.end_date || r.end);
                    const bullets = Array.isArray(r.points) ? r.points : (typeof r.points === "string" ? (() => { try { return JSON.parse(r.points || "[]"); } catch { return []; } })() : []);

                    return (
                      <div key={i} className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                          <div>
                            <span className="text-white text-sm font-semibold">{r.role}</span>
                            {r.employment_type && (
                              <span className="ml-2 text-[10px] text-blue-400 font-semibold uppercase tracking-wider">{r.employment_type}</span>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="text-xs font-mono text-zinc-500 whitespace-nowrap">
                              {startLabel} — {endLabel}
                            </span>
                            {calcDuration(r.start_date || r.start, r.end_date || r.end, r.is_current) && (
                              <span className="text-[10px] text-blue-400/60">
                                {calcDuration(r.start_date || r.start, r.end_date || r.end, r.is_current)}
                              </span>
                            )}
                          </div>
                        </div>
                        {bullets.length > 0 && (
                          <ul className="space-y-1 mt-2">
                            {bullets.map((b, bi) => (
                              <li key={bi} className="flex gap-2 text-xs text-zinc-300 leading-relaxed">
                                <span className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-blue-500" />
                                {b}
                              </li>
                            ))}
                          </ul>
                        )}
                        <ExperienceMediaCards media={r.media} cardStyle="dark" />
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Static fallback experience ────────────────────────────────────────────────
const STATIC_EXPERIENCE = [
  {
    company: "PT. Visionet Data Internasional",
    role: "Network Operations Center Engineer (PT Bank DKI)",
    start: "May 2025", end: "Present", is_current: true,
    points: [
      "Real-time monitoring using Grafana, OpManager, Dynatrace, and Elastic.",
      "Incident ticketing through Jira and Excel-based pattern mapping.",
      "Escalation to L2/L3 in 24×7 rotational shift.",
      "UiPath automations for NOC report generation.",
    ],
  },
  {
    company: "PT. Visionet Data Internasional",
    role: "Cyber Security Bootcamp (Blue & Red Team)",
    start: "Feb 2025", end: "Apr 2025", is_current: false,
    points: [
      "Blue-team: Wazuh SIEM & Kibana (ELK), Wireshark.",
      "Red-team: Nmap, Burp Suite, CTF workflows.",
    ],
  },
  {
    company: "PT. Defra Water Technology",
    role: "Reporting Officer (Freelance)",
    start: "Mar 2025", end: "Present", is_current: true,
    points: [
      "Delivered weekly/monthly reports for management decisions.",
    ],
  },
  {
    company: "PT. Defra Water Technology",
    role: "Web Developer",
    start: "Jul 2023", end: "Feb 2025", is_current: false,
    points: [
      "Built company websites with React.js + Hasura/GraphQL backend.",
      "Structured QA: functional/regression testing.",
    ],
  },
];

const STATIC_EDUCATION = [
  {
    institution: "Gunadarma University",
    degree: "Bachelor of Information Systems",
    field: "GPA 3.80",
    start_year: "2019", end_year: "2023",
    description: "",
  },
  {
    institution: "Alterra Academy",
    degree: "ReactJS Front-End Engineer",
    field: "Independent Study Program",
    start_year: "Aug 2022", end_year: "Dec 2022",
    description: "Final project: hospital website. React.js & Bootstrap.",
  },
  {
    institution: "Bangkit Academy (Google, GoTo & Traveloka)",
    degree: "Cloud Computing",
    field: "Independent Study Program",
    start_year: "Feb 2022", end_year: "Jul 2022",
    description: "Backend architecture on Google Cloud, ML via Express.js.",
  },
];

// ── Education ─────────────────────────────────────────────────────────────────
const EducationSection = ({ items }) => (
  <section className="mb-10">
    <SectionHeading
      label="Education"
      icon={
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
        </svg>
      }
    />

    <div className="relative ml-3">
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-cyan-600 via-zinc-700 to-transparent" />
      <div className="space-y-4">
        {items.map((edu, i) => {
          const startLabel = fmtDate(edu.start_date || edu.start_year);
          const endLabel = edu.end_year ? fmtDate(edu.end_year) : (edu.end_date ? fmtDate(edu.end_date) : "");
          return (
            <div key={i} className="relative pl-8">
              <div className="absolute left-0 top-1.5 -translate-x-1/2 w-3 h-3 rounded-full bg-cyan-600 border-2 border-[#111116]" />
              <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 mb-1">
                  <span className="text-cyan-400 text-sm font-black">{edu.institution}</span>
                  <span className="text-xs font-mono text-zinc-500 whitespace-nowrap">
                    {startLabel}{endLabel ? ` — ${endLabel}` : ""}
                  </span>
                </div>
                <p className="text-white text-sm font-semibold">{edu.degree}</p>
                {edu.field && <p className="text-zinc-400 text-xs mt-0.5">{edu.field}</p>}
                {edu.description && (
                  <p className="text-zinc-400 text-xs mt-2 leading-relaxed">{edu.description}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// ── Main Resume ───────────────────────────────────────────────────────────────
const Resume = () => {
  const [experiences, setExperiences] = useState(null);
  const [educations, setEducations] = useState(null);
  const [summary, setSummary] = useState("");

  useEffect(() => {
    getExperience()
      .then(setExperiences)
      .catch(() => setExperiences(STATIC_EXPERIENCE));

    getEducation()
      .then(setEducations)
      .catch(() => setEducations(STATIC_EDUCATION));

    getProfile()
      .then((d) => setSummary((d.profile ?? d)?.summary || ""))
      .catch(() => {});
  }, []);

  const expItems = experiences ?? STATIC_EXPERIENCE;
  const eduItems = educations ?? STATIC_EDUCATION;

  return (
    <div className="text-white">
      {/* Summary */}
      {summary && (
        <section className="mb-8">
          <SectionHeading
            label="Summary"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h10" />
              </svg>
            }
          />
          <p className="text-zinc-300 text-sm leading-relaxed">{summary}</p>
        </section>
      )}

      <ExperienceSection items={expItems} />
      <EducationSection items={eduItems} />
    </div>
  );
};

const SectionHeading = ({ label, icon }) => (
  <div className="flex items-center gap-2 mb-5">
    <span className="text-blue-400">{icon}</span>
    <h2 className="text-base font-black uppercase tracking-widest text-white">{label}</h2>
    <div className="flex-1 h-px bg-zinc-800 ml-2" />
  </div>
);

export default Resume;
